import ProviderRegistry from "./ProviderRegistry";

/**
 * Renders the appropriate identifier component based on the selected provider
 * @param {string} provider - The selected provider type
 * @param {Object} baseProps - Base props to pass to the identifier component
 * @returns {JSX.Element|null} - The rendered identifier component or null if provider not found
 */
export const renderProviderIdentifierComponent = (provider, baseProps) => {
  const config = ProviderRegistry.getConfig(provider);
  
  if (!config || !config.identifierComponent) {
    console.warn(`No identifier component found for provider: ${provider}`);
    return null;
  }

  const IdentifierComponent = config.identifierComponent;
  const props = config.getIdentifierProps(baseProps);

  return <IdentifierComponent {...props} />;
};

/**
 * Gets the identifier component class for a specific provider
 * @param {string} provider - The provider type
 * @returns {React.ComponentType|null} - The component class or null if not found
 */
export const getProviderIdentifierComponent = (provider) => {
  const config = ProviderRegistry.getConfig(provider);
  return config ? config.identifierComponent : null;
};

/**
 * Checks if an identifier component exists for a provider
 * @param {string} provider - The provider type to check  
 * @returns {boolean} - True if an identifier component exists
 */
export const hasProviderIdentifierComponent = (provider) => {
  const config = ProviderRegistry.getConfig(provider);
  return config && config.identifierComponent !== undefined;
};

/**
 * Gets all providers that have identifier components
 * @returns {Array} - Array of provider types that have identifier components
 */
export const getProvidersWithIdentifierComponents = () => {
  return ProviderRegistry.getAllProviders().filter(provider => 
    hasProviderIdentifierComponent(provider)
  );
};

/**
 * Validates an identifier value for a specific provider
 * @param {string} provider - The provider type
 * @param {string} value - The identifier value to validate
 * @returns {Object} - Validation result { isValid: boolean, errors: Array }
 */
export const validateProviderIdentifier = (provider, value) => {
  return ProviderRegistry.validateIdentifier(provider, value);
};

const ProviderIdentifierMapper = {
  renderProviderIdentifierComponent,
  getProviderIdentifierComponent,
  hasProviderIdentifierComponent,
  getProvidersWithIdentifierComponents,
  validateProviderIdentifier
};

export default ProviderIdentifierMapper;